// @ts-check
import { convertToShortType } from '../../../utils';
import { AURUS_GIFT_CARD_TYPES } from '../../../constants';

/**
 * @typedef {import('../types').ShippingAddressDTO} ShippingAddressDTO
 * @typedef {import('../types').BillingAddressDTO} BillingAddressDTO
 * @typedef {import('../types').CardPaymentRequest} CardPaymentRequest
 * @typedef {import('../types').GiftCardPaymentRequest} GiftCardPaymentRequest
 * @typedef {import('../types').VoidCardPaymentRequest} VoidCardPaymentRequest
 * @typedef {import('../types').VoidGiftCardPaymentRequest} VoidGiftCardPaymentRequest
 * @typedef {import('../types').AuthPaymentAddress} AuthPaymentAddress
 * @typedef {import('../types').MapCardRequestParams} MapCardRequestParams
 * @typedef {import('../types').MapGiftCardRequestParams} MapGiftCardRequestParams
 * @typedef {import('../types').MapVoidCardRequestParams} MapVoidCardRequestParams
 * @typedef {import('../types').MapVoidGiftCardRequestParams} MapVoidGiftCardRequestParams
 */

/**
 * @param {ShippingAddressDTO | BillingAddressDTO} address
 * @return {AuthPaymentAddress}
 * */
const mapAddress = ({
  line1 = '',
  line2 = '',
  country = 'US',
  city = '',
  state = '',
  zip = '',
  phone = '',
  firstName = '',
  lastName = '',
}) => ({
  addressLine1: line1,
  addressLine2: line2,
  mobileNumber: phone,
  country,
  city,
  state,
  firstName,
  lastName,
  zip,
});

const mapBaseData = ({
  items,
  shippingAddress,
  productTotalAmount,
  productTotalTax,
  transactionTotal,
  invoiceNumber,
  transactionNumber,
}) => {
  return {
    items,
    productTotalTax,
    productTotalAmount,
    transactionTotal,
    invoiceNumber,
    transactionNumber: transactionNumber.toString(),
    shippingAddresses: [mapAddress(shippingAddress)],
  };
};

/**
 * @param {MapCardRequestParams} params
 * @return {CardPaymentRequest}
 * */
export const mapCardRequest = ({
  items,
  payment,
  shippingAddress,
  productTotalAmount,
  productTotalTax,
  transactionTotal,
  invoiceNumber,
  transactionNumber = '',
}) => {
  const baseData = mapBaseData({
    items,
    shippingAddress,
    productTotalAmount,
    productTotalTax,
    transactionTotal,
    invoiceNumber,
    transactionNumber,
  });

  return {
    ...baseData,
    cardType: convertToShortType(payment.networkType),
    billingAddress: mapAddress(payment.billingAddress),
    keyIdentifier: payment.keyIdentifier,
  };
};

/**
 * @param {MapGiftCardRequestParams} params
 * @return {GiftCardPaymentRequest}
 * */
export const mapGiftCardRequest = ({
  items,
  giftCardData,
  shippingAddress,
  productTotalAmount,
  productTotalTax,
  transactionTotal,
  invoiceNumber,
  transactionNumber = '',
}) => {
  const baseData = mapBaseData({
    items,
    shippingAddress,
    productTotalAmount,
    productTotalTax,
    transactionTotal,
    invoiceNumber,
    transactionNumber,
  });

  return {
    ...baseData,
    // For gift card payment we must use this gift card type
    cardType: AURUS_GIFT_CARD_TYPES.GCC,
    giftCardNumber: giftCardData.giftCardNumber,
    giftCardPIN: giftCardData.giftCardPin,
  };
};

/**
 * @param {MapVoidCardRequestParams} params
 * @return {VoidCardPaymentRequest}
 * */
export const mapVoidCardRequest = ({
  items,
  payment,
  shippingAddress,
  productTotalAmount,
  productTotalTax,
  invoiceNumber,
  transactionNumber = '',
}) => {
  const { aurusPaymentInformation } = payment;
  const baseData = mapCardRequest({
    items,
    payment,
    shippingAddress,
    productTotalAmount,
    productTotalTax,
    transactionTotal: aurusPaymentInformation.totalApprovedAmount || '0.00',
    invoiceNumber,
    transactionNumber,
  });

  return {
    ...baseData,
    origAurusPayTicketNum: aurusPaymentInformation.aurusPayTicketNum,
    origTransactionIdentifier: aurusPaymentInformation.aurusPayTransactionId,
  };
};

/**
 * @param {MapVoidGiftCardRequestParams} params
 * @return {VoidGiftCardPaymentRequest}
 * */
export const mapVoidGiftCardRequest = ({
  items,
  payment,
  shippingAddress,
  productTotalAmount,
  productTotalTax,
  invoiceNumber,
  transactionNumber = '',
}) => {
  const { aurusPaymentInformation } = payment;
  const baseData = mapGiftCardRequest({
    items,
    giftCardData: payment,
    shippingAddress,
    productTotalAmount,
    productTotalTax,
    transactionTotal: aurusPaymentInformation.totalApprovedAmount || '0.00',
    invoiceNumber,
    transactionNumber,
  });

  return {
    ...baseData,
    origAurusPayTicketNum: aurusPaymentInformation.aurusPayTicketNum,
    origTransactionIdentifier: aurusPaymentInformation.aurusPayTransactionId,
  };
};
